/**
 * DonorPanel — Teilespender (Fahrzeuge zum Zerlegen), Jun-2026.
 *
 * Listet Fahrzeug-Inserate mit Zustand „Schrott / Verwertung / Export" und
 * zeigt je Spender: demontierte/nicht versendbare Teile, daraus angelegte
 * WMS-Artikel, Fahrzeugbilder + Zustandsfotos mit Datum sowie die gescannten
 * Fahrzeugpapiere (ZB1).
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
    Loader2, ArrowLeft, Car, Wrench, Camera, FileText, Plus, Trash2,
    Package, ExternalLink, Image as ImageIcon,
} from "lucide-react";
import { API } from "../../lib/api";
import api from "../../lib/api";
import { normalizeImageUrl } from "../../lib/imageUrl";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import ModalShell from "../../pages/wms/ModalShell";

const COND = {
    demontiert: { label: "Demontiert", cls: "bg-blue-100 text-blue-800" },
    beschaedigt: { label: "Beschädigt", cls: "bg-amber-100 text-amber-800" },
    nicht_versendbar: { label: "Nicht versendbar", cls: "bg-red-100 text-red-800" },
};

const photoUrl = (path) => `${API}/wms/photos/${path}?auth=${sessionStorage.getItem("tw_token") || ""}`;
const docUrl = (d) => (typeof d === "string" ? d : d?.url);
const docName = (d, i) => (typeof d === "string" ? `Dokument ${i + 1}` : (d?.name || `Dokument ${i + 1}`));

export default function DonorPanel({ open, onClose, onCreateArticle }) {
    const [donors, setDonors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selId, setSelId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailBusy, setDetailBusy] = useState(false);
    // Teil-Formular
    const [pName, setPName] = useState("");
    const [pCond, setPCond] = useState("demontiert");
    const [pNote, setPNote] = useState("");
    const [photoDate, setPhotoDate] = useState(new Date().toISOString().slice(0, 10));
    const [notes, setNotes] = useState("");

    const loadList = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/wms/donors");
            setDonors(data.donors || []);
        } catch { toast.error("Teilespender konnten nicht geladen werden"); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        if (!open) return;
        let on = true;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/wms/donors");
                if (on) setDonors(data.donors || []);
            } catch { toast.error("Teilespender konnten nicht geladen werden"); }
            finally { if (on) setLoading(false); }
        })();
        return () => { on = false; };
    }, [open]);

    const loadDetail = useCallback(async (id) => {
        setDetailBusy(true);
        try {
            const { data } = await api.get(`/wms/donors/${id}`);
            setDetail(data);
            setNotes(data.notes || "");
        } catch { toast.error("Detail konnte nicht geladen werden"); }
        finally { setDetailBusy(false); }
    }, []);

    const openDetail = (id) => { setSelId(id); loadDetail(id); };
    const backToList = () => { setSelId(null); setDetail(null); loadList(); };

    const addPart = async () => {
        if (!pName.trim()) { toast.error("Bezeichnung erforderlich"); return; }
        try {
            await api.post(`/wms/donors/${selId}/parts`, { name: pName, condition: pCond, note: pNote });
            setPName(""); setPNote(""); setPCond("demontiert");
            toast.success("Teil erfasst");
            loadDetail(selId);
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    };

    const delPart = async (pid) => {
        try { await api.delete(`/wms/donors/${selId}/parts/${pid}`); loadDetail(selId); }
        catch { toast.error("Löschen fehlgeschlagen"); }
    };

    const saveNotes = async () => {
        try { await api.patch(`/wms/donors/${selId}`, { notes }); toast.success("Notiz gespeichert"); }
        catch { toast.error("Speichern fehlgeschlagen"); }
    };

    const uploadAndAttach = async (file, kind) => {
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/wms/photos/upload", fd);
            if (kind === "photo") {
                await api.post(`/wms/donors/${selId}/photos`, { path: data.path, taken_at: photoDate });
                toast.success("Zustandsfoto hinzugefügt");
            } else {
                await api.post(`/wms/donors/${selId}/papers`, { path: data.path, name: file.name, kind: "scan" });
                toast.success("Papier hinzugefügt");
            }
            loadDetail(selId);
        } catch (e) { toast.error(e.response?.data?.detail || "Upload fehlgeschlagen"); }
    };

    const delPhoto = async (id) => {
        try { await api.delete(`/wms/donors/${selId}/photos/${id}`); loadDetail(selId); }
        catch { toast.error("Löschen fehlgeschlagen"); }
    };
    const delPaper = async (id) => {
        try { await api.delete(`/wms/donors/${selId}/papers/${id}`); loadDetail(selId); }
        catch { toast.error("Löschen fehlgeschlagen"); }
    };

    if (!open) return null;

    const title = selId ? "Teilespender · Detail" : "Teilespender (Fahrzeuge zum Zerlegen)";
    const v = detail?.vehicle;
    const vLabel = v ? `${v.brand || ""} ${v.model || ""} ${v.year || ""}`.trim() : "";

    return (
        <ModalShell onClose={onClose} title={title} wide testid="donor-panel">
            {/* LISTE */}
            {!selId && (
                loading ? (
                    <div className="py-10 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Lade Teilespender…</div>
                ) : donors.length === 0 ? (
                    <div className="py-12 text-center" data-testid="donor-empty">
                        <Car className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <div className="font-display font-black text-lg">Keine Teilespender</div>
                        <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
                            Markiere ein Fahrzeug beim Inserieren mit dem Zustand <strong>„Schrott / Verwertung / Export“</strong>,
                            damit es hier als Teilespender erscheint.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="donor-grid">
                        {donors.map((d) => (
                            <button
                                key={d.listing_id}
                                type="button"
                                onClick={() => openDetail(d.listing_id)}
                                className="text-left bg-white border border-gray-200 rounded-sm overflow-hidden hover:border-[#FFCC00] hover:shadow-md transition-all"
                                data-testid={`donor-card-${d.listing_id}`}
                            >
                                <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center overflow-hidden">
                                    {d.primary_image
                                        ? <img src={normalizeImageUrl(d.primary_image)} alt={d.title} className="w-full h-full object-cover" />
                                        : <ImageIcon className="w-10 h-10 text-gray-300" />}
                                </div>
                                <div className="p-2.5">
                                    <div className="font-bold text-sm truncate">{d.brand} {d.model} {d.year ? `· ${d.year}` : ""}</div>
                                    {d.vin && <div className="font-mono-num text-[10px] text-gray-500 truncate">FIN {d.vin}</div>}
                                    <div className="flex flex-wrap gap-1 mt-1.5 text-[10px]">
                                        <span className="bg-gray-100 px-1.5 py-0.5 rounded-sm"><Wrench className="w-3 h-3 inline mr-0.5" />{d.parts_count} Teile</span>
                                        <span className="bg-gray-100 px-1.5 py-0.5 rounded-sm"><Package className="w-3 h-3 inline mr-0.5" />{d.linked_articles_count} Artikel</span>
                                        <span className="bg-gray-100 px-1.5 py-0.5 rounded-sm"><Camera className="w-3 h-3 inline mr-0.5" />{d.photos_count + d.image_count}</span>
                                        <span className="bg-gray-100 px-1.5 py-0.5 rounded-sm"><FileText className="w-3 h-3 inline mr-0.5" />{d.papers_count}</span>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                )
            )}

            {/* DETAIL */}
            {selId && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <Button variant="outline" size="sm" className="rounded-sm" onClick={backToList} data-testid="donor-back">
                            <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
                        </Button>
                        {v && (
                            <Button size="sm" className="rounded-sm bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold"
                                    onClick={() => onCreateArticle?.(selId, vLabel)} data-testid="donor-create-article">
                                <Plus className="w-4 h-4 mr-1" /> Artikel aus Spender anlegen
                            </Button>
                        )}
                    </div>

                    {detailBusy || !v ? (
                        <div className="py-8 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Lade…</div>
                    ) : (
                        <>
                            {/* Fahrzeug-Kopf */}
                            <div className="bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="donor-vehicle">
                                <div className="font-display font-black text-lg">{vLabel || v.title}</div>
                                <div className="text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-1 mt-1">
                                    {v.vin && <span>FIN: <strong className="font-mono-num">{v.vin}</strong></span>}
                                    {v.mileage != null && <span>{Number(v.mileage).toLocaleString("de-DE")} km</span>}
                                    {v.first_registration_date && <span>EZ: {v.first_registration_date}</span>}
                                    <span className="text-red-700 font-bold">{v.condition}</span>
                                    {v.has_zb1 && <span className="text-emerald-700">ZB1 vorhanden{v.has_zb1_date ? ` (${v.has_zb1_date})` : ""}</span>}
                                </div>
                            </div>

                            {/* Bilder + Zustandsfotos */}
                            <Section icon={Camera} title={`Fahrzeugbilder & Zustandsfotos`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Foto-Datum</span>
                                    <Input type="date" value={photoDate} onChange={(e) => setPhotoDate(e.target.value)} className="rounded-sm h-7 w-40 text-xs" data-testid="donor-photo-date" />
                                    <label className="text-[11px] font-bold text-[#111827] border border-gray-300 rounded-sm px-2 py-1 cursor-pointer hover:bg-gray-50" data-testid="donor-photo-upload-label">
                                        <Camera className="w-3 h-3 inline mr-1" /> Zustandsfoto
                                        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadAndAttach(e.target.files[0], "photo")} />
                                    </label>
                                </div>
                                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                                    {(v.images || []).map((img, i) => (
                                        <div key={`l${i}`} className="relative aspect-square bg-gray-100 rounded-sm overflow-hidden border border-gray-200">
                                            <img src={normalizeImageUrl(img)} alt="" className="w-full h-full object-cover" />
                                            <span className="absolute bottom-0 left-0 right-0 bg-black/80 text-white text-[8px] px-1 py-0.5">Inserat{v.image_labels?.[i] ? ` · ${v.image_labels[i]}` : ""}</span>
                                        </div>
                                    ))}
                                    {(detail.photos || []).map((p) => (
                                        <div key={p.id} className="relative aspect-square bg-gray-100 rounded-sm overflow-hidden border border-purple-200" data-testid={`donor-photo-${p.id}`}>
                                            <img src={photoUrl(p.path)} alt="" className="w-full h-full object-cover" />
                                            <span className="absolute bottom-0 left-0 right-0 bg-purple-700/80 text-white text-[8px] px-1 py-0.5">{p.taken_at}</span>
                                            <button type="button" onClick={() => delPhoto(p.id)} className="absolute top-0.5 right-0.5 bg-white/90 rounded-sm p-0.5 text-red-600 hover:bg-white" data-testid={`donor-photo-del-${p.id}`}>
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                    {(v.images || []).length === 0 && (detail.photos || []).length === 0 && (
                                        <div className="col-span-full text-xs text-gray-400 py-3 text-center">Noch keine Bilder.</div>
                                    )}
                                </div>
                            </Section>

                            {/* Papiere */}
                            <Section icon={FileText} title="Gescannte Fahrzeugpapiere">
                                <label className="text-[11px] font-bold text-[#111827] border border-gray-300 rounded-sm px-2 py-1 cursor-pointer hover:bg-gray-50 inline-block mb-2" data-testid="donor-paper-upload-label">
                                    <FileText className="w-3 h-3 inline mr-1" /> Papier hochladen
                                    <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files?.[0] && uploadAndAttach(e.target.files[0], "paper")} />
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {(v.document_urls || []).map((d, i) => (
                                        <a key={`d${i}`} href={docUrl(d)} target="_blank" rel="noreferrer" className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-sm px-2 py-1 hover:bg-emerald-100" data-testid={`donor-doc-${i}`}>
                                            <FileText className="w-3 h-3 inline mr-1" />{docName(d, i)} <ExternalLink className="w-3 h-3 inline" />
                                        </a>
                                    ))}
                                    {(detail.papers || []).map((p) => (
                                        <span key={p.id} className="text-xs bg-gray-50 border border-gray-200 rounded-sm px-2 py-1 inline-flex items-center gap-1" data-testid={`donor-paper-${p.id}`}>
                                            <a href={photoUrl(p.path)} target="_blank" rel="noreferrer" className="hover:underline"><FileText className="w-3 h-3 inline mr-1" />{p.name}</a>
                                            <button type="button" onClick={() => delPaper(p.id)} className="text-red-600"><Trash2 className="w-3 h-3" /></button>
                                        </span>
                                    ))}
                                    {(v.document_urls || []).length === 0 && (detail.papers || []).length === 0 && (
                                        <span className="text-xs text-gray-400">Keine Papiere hinterlegt.</span>
                                    )}
                                </div>
                            </Section>

                            {/* Teile */}
                            <Section icon={Wrench} title={`Teile (${(detail.parts || []).length})`}>
                                <div className="flex flex-wrap gap-2 mb-3 items-end">
                                    <div className="flex-1 min-w-[160px]">
                                        <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Bezeichnung</label>
                                        <Input value={pName} onChange={(e) => setPName(e.target.value)} placeholder="z. B. Vorderrad, Tank, Scheinwerfer" className="rounded-sm" data-testid="donor-part-name" />
                                    </div>
                                    <div className="w-44">
                                        <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Zustand</label>
                                        <Select value={pCond} onValueChange={setPCond}>
                                            <SelectTrigger className="rounded-sm" data-testid="donor-part-cond"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="demontiert">Demontiert</SelectItem>
                                                <SelectItem value="beschaedigt">Beschädigt</SelectItem>
                                                <SelectItem value="nicht_versendbar">Nicht versendbar</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex-1 min-w-[140px]">
                                        <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Notiz</label>
                                        <Input value={pNote} onChange={(e) => setPNote(e.target.value)} placeholder="optional" className="rounded-sm" data-testid="donor-part-note" />
                                    </div>
                                    <Button onClick={addPart} className="rounded-sm bg-[#111827] hover:bg-black text-white font-bold" data-testid="donor-part-add">
                                        <Plus className="w-4 h-4 mr-1" /> Teil
                                    </Button>
                                </div>
                                {(detail.parts || []).length === 0 ? (
                                    <div className="text-xs text-gray-400 py-2">Noch keine Teile erfasst.</div>
                                ) : (
                                    <div className="divide-y divide-gray-100 border border-gray-200 rounded-sm">
                                        {detail.parts.map((p) => (
                                            <div key={p.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`donor-part-${p.id}`}>
                                                <span className="font-bold flex-1">{p.name}</span>
                                                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${COND[p.condition]?.cls || "bg-gray-100"}`}>{COND[p.condition]?.label || p.condition}</span>
                                                {p.note && <span className="text-[11px] text-gray-500 hidden sm:inline">{p.note}</span>}
                                                <button type="button" onClick={() => delPart(p.id)} className="text-red-600 hover:bg-red-50 rounded-sm p-1" data-testid={`donor-part-del-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Section>

                            {/* Verknüpfte Artikel */}
                            <Section icon={Package} title={`Daraus angelegte Artikel (${(detail.linked_articles || []).length})`}>
                                {(detail.linked_articles || []).length === 0 ? (
                                    <div className="text-xs text-gray-400 py-2">Noch keine Artikel mit Herkunft auf diesen Spender.</div>
                                ) : (
                                    <div className="divide-y divide-gray-100 border border-gray-200 rounded-sm">
                                        {detail.linked_articles.map((a) => (
                                            <div key={a.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`donor-art-${a.id}`}>
                                                <span className="font-mono-num text-[11px] text-gray-500">{a.sku}</span>
                                                <span className="font-bold flex-1 truncate">{a.name}</span>
                                                <span className="font-mono-num text-xs">{Number(a.stock_qty || 0)} {a.unit || "Stück"}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Section>

                            {/* Notiz */}
                            <Section icon={FileText} title="Notiz zum Spender">
                                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="z. B. Demontage-Status, Standort, Besonderheiten" className="rounded-sm" data-testid="donor-notes" />
                                <Button size="sm" variant="outline" className="rounded-sm mt-2" onClick={saveNotes} data-testid="donor-notes-save">Notiz speichern</Button>
                            </Section>
                        </>
                    )}
                </div>
            )}
        </ModalShell>
    );
}

function Section({ icon: Icon, title, children }) {
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-3">
            <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4 text-[#111827]" />
                <h4 className="font-bold text-sm">{title}</h4>
            </div>
            {children}
        </div>
    );
}
